/* eslint-disable sonarjs/cognitive-complexity */
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useLayoutEffect,
} from 'react';
import ReactDOM from 'react-dom';
import { makeStyles, Typography, RACCOLOR } from '@rentacenter/racstrap';
import CircularProgress from '@material-ui/core/CircularProgress';
import { ReactComponent as SearchImageDefault } from '../../assets/images/Search-icon-Grey.svg';

interface DropdownOption {
  label: string;
  value: string;
}

export interface DropdownProps {
  options: DropdownOption[];
  selection: (value: string) => void;
  defaultValue?: string;
  value?: string;
  delimiter?: string;
  isMulti?: boolean;
  loading: boolean;
  inputLabel: string;
  isDisabled: boolean;
  required: boolean;
  readOnly?: boolean;
  resultLimit?: number;
  minChars?: number;
  showSearchIcon?: boolean;
}

const useStyles = makeStyles((theme: any) => ({
  dropdown: {
    position: 'relative',
    border: '1px solid #c4c4c4',
    borderRadius: theme.typography.pxToRem(6),
    padding: theme.typography.pxToRem(7),
    background: 'white',
    fontFamily: 'OpenSans-regular',
    fontSize: theme.typography.pxToRem(14),
    marginTop: theme.typography.pxToRem(8),
    zIndex: 1,
  },
  selected: {
    display: 'flex',
    alignItems: 'center',
    color: RACCOLOR.WASHED_BLACK,
  },
  inputBox: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: theme.typography.pxToRem(14),
    backgroundColor: 'transparent',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '&::placeholder': {
      color: RACCOLOR.WASHED_BLACK,
      opacity: 1,
    },
    '&::-webkit-input-placeholder': {
      color: RACCOLOR.WASHED_BLACK,
      opacity: 1,
    },
    '&::-moz-placeholder': {
      color: RACCOLOR.WASHED_BLACK,
      opacity: 1,
    },
    '&:-ms-input-placeholder': {
      color: RACCOLOR.WASHED_BLACK,
      opacity: 1,
    },
    '&::-ms-input-placeholder': {
      color: RACCOLOR.WASHED_BLACK,
      opacity: 1,
    },
  },
  clearIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.typography.pxToRem(18),
    height: theme.typography.pxToRem(18),
    marginLeft: theme.typography.pxToRem(6),
    borderRadius: '50%',
    cursor: 'pointer',
    color: '#666',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
    userSelect: 'none',
  },
  arrow: {
    marginLeft: theme.typography.pxToRem(8),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  options: {
    position: 'fixed',
    background: RACCOLOR.WHITE,
    border: '1px solid #c4c4c4',
    borderRadius: theme.typography.pxToRem(4),
    zIndex: 2147483647,
    listStyleType: 'none',
    maxHeight: '260px',
    overflowY: 'auto',
    padding: 0,
    margin: 0,
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
    pointerEvents: 'auto',
  },
  option: {
    padding: theme.typography.pxToRem(10),
    cursor: 'pointer',
    fontWeight: 200,
    display: 'flex',
    alignItems: 'center',
    gap: theme.typography.pxToRem(8),
    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
  },
  optionActive: { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
  disabled: { backgroundColor: 'rgb(235, 235, 228)', cursor: 'default' },
  formLabel: {
    fontFamily: 'OpenSans-semibold',
    color: RACCOLOR.WASHED_BLACK,
    fontSize: theme.typography.pxToRem(14),
  },
  selectLabel: { backgroundColor: '#f0f0f0' },
  loading: {
    color: theme.palette.secondary.main,
    width: `${theme.typography.pxToRem(15)} !important`,
    height: `${theme.typography.pxToRem(15)} !important`,
    margin: '0.5px 0.5px 0 0',
  },
  reqField: { color: 'red', marginLeft: theme.typography.pxToRem(4) },
}));

const isEmpty = (s?: string) => String(s ?? '').trim() === '';

const normalizeOptions = (options: DropdownOption[]) =>
  (options ?? []).map((o) => ({ ...o, _l: o.label.toLowerCase() }));

const filterOptions = (
  normalized: (DropdownOption & { _l: string })[],
  query: string,
  minChars: number,
  limit: number
) => {
  const q = (query ?? '').trim();
  if (q.length < minChars)
    return normalized
      .slice(0, limit)
      .map(({ label, value }) => ({ label, value }));

  const ql = q.toLowerCase();
  const out: DropdownOption[] = [];
  for (let i = 0; i < normalized.length; i++) {
    const item = normalized[i];
    if (item._l.includes(ql)) {
      out.push({ label: item.label, value: item.value });
      if (out.length >= limit) break;
    }
  }
  return out;
};

const findSelectedIndex = (options: DropdownOption[], v: string) =>
  options.findIndex((o) => o.value === v);

const splitValues = (v: string, delimiter: string) =>
  (v ?? '')
    .split(delimiter)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

const joinValues = (vals: Set<string>, delimiter: string) =>
  Array.from(vals).join(delimiter);

export const SearchableDropdown: React.FC<DropdownProps> = ({
  options,
  selection,
  defaultValue,
  value,
  delimiter = ',',
  isMulti = false,
  loading,
  inputLabel,
  isDisabled,
  required,
  readOnly = false,
  resultLimit = 200,
  minChars = 0,
  showSearchIcon = true,
}) => {
  const classes = useStyles();

  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [currentValue, setCurrentValue] = useState<string>('');

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);
  const [menuPos, setMenuPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const normalizedOptions = useMemo(
    () => normalizeOptions(options ?? []),
    [options]
  );
  const isControlled = typeof value !== 'undefined';

  useEffect(() => {
    if (isControlled) {
      setCurrentValue(value ?? '');
    } else {
      setCurrentValue(defaultValue ?? '');
    }
    setIsTyping(false);
    setSearchText('');
  }, [isControlled, value, defaultValue]);

  const filteredOptions = useMemo(
    () => filterOptions(normalizedOptions, searchText, minChars, resultLimit),
    [normalizedOptions, searchText, minChars, resultLimit]
  );

  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(-1);
      return;
    }
    if (isMulti) {
      setActiveIndex(0);
      return;
    }
    const selected = isControlled ? value ?? '' : currentValue;
    const idx = findSelectedIndex(filteredOptions, selected);
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [isOpen, filteredOptions, isMulti, isControlled, value, currentValue]);

  const openList = useCallback(() => {
    if (isDisabled || loading) return;
    setIsOpen(true);
  }, [isDisabled, loading]);

  const closeList = useCallback(() => setIsOpen(false), []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly) return;
      const next = e.target.value;
      setSearchText(next);
      setIsTyping(true);
      openList();
      if (!isMulti && isEmpty(next) && !isEmpty(currentValue)) {
        if (isControlled) selection('');
        else {
          setCurrentValue('');
          selection('');
        }
      }
    },
    [readOnly, openList, isMulti, currentValue, isControlled, selection]
  );

  const selectSingle = useCallback(
    (label: string, v: string) => {
      if (isDisabled || loading || readOnly) return;
      if (isControlled) selection(v);
      else {
        setCurrentValue(v);
        selection(v);
      }
      setIsTyping(false);
      setSearchText('');
      closeList();
    },
    [isDisabled, loading, readOnly, isControlled, selection, closeList]
  );

  const toggleMulti = useCallback(
    (label: string, v: string) => {
      if (isDisabled || loading || readOnly) return;
      const base = isControlled ? value ?? '' : currentValue;
      const set = new Set(splitValues(base, delimiter));
      if (set.has(v)) set.delete(v);
      else set.add(v);
      const next = joinValues(set, delimiter);
      if (isControlled) selection(next);
      else {
        setCurrentValue(next);
        selection(next);
      }
    },
    [
      isDisabled,
      loading,
      readOnly,
      isControlled,
      value,
      currentValue,
      delimiter,
      selection,
    ]
  );

  const toggleSelectAll = useCallback(() => {
    if (!isMulti || isDisabled || loading || readOnly) return;
    const onScreen = filteredOptions.map((o) => o.value);
    const base = isControlled ? value ?? '' : currentValue;
    const set = new Set(splitValues(base, delimiter));
    const allSelected =
      onScreen.length > 0 && onScreen.every((v) => set.has(v));
    if (allSelected) onScreen.forEach((v) => set.delete(v));
    else onScreen.forEach((v) => set.add(v));
    const next = joinValues(set, delimiter);
    if (isControlled) selection(next);
    else {
      setCurrentValue(next);
      selection(next);
    }
  }, [
    isMulti,
    isDisabled,
    loading,
    readOnly,
    filteredOptions,
    isControlled,
    value,
    currentValue,
    delimiter,
    selection,
  ]);

  useLayoutEffect(() => {
    const update = () => {
      const rect = dropdownRef.current?.getBoundingClientRect();
      if (rect)
        setMenuPos({ top: rect.bottom, left: rect.left, width: rect.width });
    };
    if (isOpen) {
      update();
      window.addEventListener('scroll', update, true);
      window.addEventListener('resize', update);
      return () => {
        window.removeEventListener('scroll', update, true);
        window.removeEventListener('resize', update);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideField = dropdownRef.current?.contains(target);
      const insideMenu = menuRef.current?.contains(target);
      if (!insideField && !insideMenu) {
        closeList();
        if (isEmpty(searchText)) setIsTyping(false);
      }
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [closeList, searchText]);

  const keyHandlers = useMemo<
    Record<string, (e: React.KeyboardEvent<HTMLInputElement>) => void>
  >(
    () => ({
      ArrowDown: (e) => {
        if (!isOpen || isDisabled || loading) return;
        const max = isMulti
          ? filteredOptions.length + 1
          : filteredOptions.length;
        setActiveIndex((prev) => Math.min(prev + 1, max - 1));
        setIsTyping(true);
        e.preventDefault();
      },
      ArrowUp: (e) => {
        if (!isOpen || isDisabled || loading) return;
        setActiveIndex((prev) => Math.max(prev - 1, 0));
        setIsTyping(true);
        e.preventDefault();
      },
      Enter: (e) => {
        if (!isOpen || isDisabled || loading) return;
        if (isMulti) {
          if (activeIndex === 0) toggleSelectAll();
          else {
            const opt = filteredOptions[activeIndex - 1];
            if (opt) toggleMulti(opt.label, opt.value);
          }
        } else {
          const opt = filteredOptions[activeIndex];
          if (opt) selectSingle(opt.label, opt.value);
        }
        e.preventDefault();
      },
      Escape: (e) => {
        if (!isOpen || isDisabled || loading) return;
        closeList();
        setIsTyping(false);
        e.preventDefault();
      },
    }),
    [
      isOpen,
      isDisabled,
      loading,
      filteredOptions,
      activeIndex,
      isMulti,
      toggleSelectAll,
      toggleMulti,
      selectSingle,
      closeList,
    ]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const handler = keyHandlers[e.key];
      if (handler) {
        handler(e);
        return;
      }
      if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        openList();
        setIsTyping(true);
        e.preventDefault();
      }
    },
    [keyHandlers, isOpen, openList]
  );

  const selectedSet: Set<string> = useMemo(() => {
    const v = isControlled ? value ?? '' : currentValue;
    return isMulti ? new Set(splitValues(v, delimiter)) : new Set(v ? [v] : []);
  }, [isControlled, value, currentValue, isMulti, delimiter]);

  const selectedOptions: DropdownOption[] = useMemo(() => {
    if (selectedSet.size === 0) return [];
    const lookup = new Set(selectedSet);
    return (options ?? []).filter((o) => lookup.has(o.value));
  }, [selectedSet, options]);

  const singleLabel = useMemo(() => {
    if (isMulti) return '';
    const v = isControlled ? value ?? '' : currentValue;
    const matched = (options ?? []).find((o) => o.value === v);
    return matched?.label ?? '';
  }, [isMulti, isControlled, value, currentValue, options]);

  const displayValue = useMemo(() => {
    if (!isMulti) return singleLabel;
    if (selectedOptions.length === 0) return '';
    const labels = selectedOptions.map((s) => s.label);
    if (labels.length === 1) return labels[0];
    return `${labels[0]} ${labels[1]}...`;
  }, [isMulti, selectedOptions, singleLabel]);

  const hasAnySelection = isMulti
    ? selectedOptions.length > 0
    : !isEmpty(isControlled ? value : currentValue);
  const showSelectedText =
    !isMulti && hasAnySelection && !isTyping && isEmpty(searchText);
  const inputValue = isMulti
    ? isTyping
      ? searchText
      : displayValue
    : showSelectedText
    ? singleLabel
    : searchText;
  const placeholder = isTyping ? '' : 'Select';

  const allOnScreenSelected = useMemo(() => {
    if (!isMulti) return false;
    if (filteredOptions.length === 0) return false;
    return filteredOptions.every((o) => selectedSet.has(o.value));
  }, [isMulti, filteredOptions, selectedSet]);

  const hasSelectionForClear = useMemo(() => {
    if (loading || isDisabled || readOnly) return false;
    if (isTyping && !isEmpty(searchText)) return false;
    if (isMulti) return selectedOptions.length > 0;
    const v = isControlled ? value ?? '' : currentValue;
    return !isEmpty(v);
  }, [
    loading,
    isDisabled,
    readOnly,
    isTyping,
    searchText,
    isMulti,
    selectedOptions,
    isControlled,
    value,
    currentValue,
  ]);

  const handleClearSelection = useCallback(
    (e?: React.MouseEvent) => {
      if (e) e.stopPropagation();
      if (isDisabled || loading || readOnly) return;
      if (isControlled) selection('');
      else {
        setCurrentValue('');
        selection('');
      }
      setSearchText('');
      setIsTyping(false);
    },
    [isDisabled, loading, readOnly, isControlled, selection]
  );

  return (
    <>
      <Typography
        variant="body2"
        component="label"
        className={classes.formLabel}
      >
        {inputLabel}
        {required && <span className={classes.reqField}>*</span>}
      </Typography>

      <div
        className={`${classes.dropdown} ${
          isDisabled || loading ? classes.disabled : ''
        }`}
        ref={dropdownRef}
      >
        <div className={classes.selected}>
          <input
            type="text"
            className={classes.inputBox}
            value={inputValue}
            placeholder={placeholder}
            onChange={handleInputChange}
            onClick={openList}
            onKeyDown={handleKeyDown}
            disabled={isDisabled || loading}
            readOnly={readOnly}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls="searchable-dropdown-listbox"
            role="combobox"
          />

          {hasSelectionForClear && isMulti && (
            <span
              className={classes.clearIcon}
              role="button"
              aria-label="Clear selection"
              onClick={handleClearSelection}
              onMouseDown={(e) => e.preventDefault()}
            >
              X
            </span>
          )}

          {loading ? (
            <CircularProgress className={classes.loading} />
          ) : showSearchIcon ? (
            <span className={classes.arrow}>
              <SearchImageDefault onClick={openList} />
            </span>
          ) : (
            <span className={classes.arrow}>
              <svg
                fill="#000000"
                height="9px"
                width="10px"
                viewBox="0 0 407.437 407.437"
              >
                <polygon points="386.258,91.567 203.718,273.512 21.179,91.567 0,112.815 203.718,315.87 407.437,112.815" />
              </svg>
            </span>
          )}
        </div>
      </div>

      {isOpen &&
        menuPos &&
        ReactDOM.createPortal(
          <ul
            id="searchable-dropdown-listbox"
            role="listbox"
            aria-multiselectable={isMulti || undefined}
            ref={menuRef}
            className={classes.options}
            style={{
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {isMulti && (
              <li
                key="__select_all__"
                onMouseDown={(e) => {
                  e.preventDefault();
                  toggleSelectAll();
                }}
                className={`${classes.option} ${
                  activeIndex === 0 ? classes.optionActive : ''
                }`}
                role="option"
                aria-selected={allOnScreenSelected}
              >
                <input
                  type="checkbox"
                  tabIndex={-1}
                  readOnly
                  checked={allOnScreenSelected}
                />
                <span>Select All</span>
              </li>
            )}
            {filteredOptions.map((option, idx) => {
              const isActive =
                idx === (isMulti ? activeIndex - 1 : activeIndex);
              const isSelected = selectedSet.has(option.value);
              return (
                <li
                  key={option.value}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (isMulti) toggleMulti(option.label, option.value);
                    else selectSingle(option.label, option.value);
                  }}
                  className={`${classes.option} ${
                    isActive ? classes.optionActive : ''
                  } ${isSelected ? classes.selectLabel : ''}`}
                  role="option"
                  aria-selected={isSelected}
                >
                  {isMulti && (
                    <input
                      type="checkbox"
                      tabIndex={-1}
                      checked={isSelected}
                      readOnly
                    />
                  )}
                  <span>{option.label}</span>
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </>
  );
};
